import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, Input, OnDestroy, Signal, signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DsButtonVariants, DsModule } from '@levelaccess/design-system';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { $digitalProperty } from '../../../../../../shared/constants/digital-properties';
import { $workspace } from '../../../../../../shared/constants/workspace';
import { IDigitalPropertyListItem } from '../../../../../../shared/interfaces/digital-property.interface';
import { IWorkspaceClient } from '../../../../../../shared/interfaces/workspace.interface';
import { DigitalPropertyCollisionUtility } from '../../../../../../shared/utils/digital-property-collision.utility';
import { BusMessageService } from '../../../../services/bus-message.service';
import { DigitalPropertyService } from '../../../../services/digital-property.service';
import { LoadErrorHandler } from '../../../../services/load-error-handler';
import { CommonPipesModule } from '../../../../shared/common-pipes.module';

@Component({
  selector: 'app-move-digital-property-modal',
  templateUrl: './move-digital-property-modal.component.html',
  imports: [DsModule, CommonPipesModule, FormsModule],
  providers: [BusMessageService],
  standalone: true,
})
export class MoveDigitalPropertyModalComponent implements OnDestroy {
  @Input() workspaceId: string;
  @Input() availableWorkspaces: IWorkspaceClient[] = [];
  @Input() digitalProperty: IDigitalPropertyListItem;
  @Input() allDigitalProperties: IDigitalPropertyListItem[] = [];

  destinationWorkspaceId: WritableSignal<string> = signal<string>(null);
  destinationDigitalProperties: Signal<IDigitalPropertyListItem[]> = computed(() => {
    const destinationId: string = this.destinationWorkspaceId();
    if (!destinationId) {
      return [];
    }
    return this.allDigitalProperties.filter(
      (dp: IDigitalPropertyListItem) => dp[$digitalProperty.workspace][$workspace._id].toString() === destinationId,
    );
  });
  hasKeyCollision: Signal<boolean> = computed(() =>
    DigitalPropertyCollisionUtility.hasKeyCollision(this.digitalProperty, this.destinationDigitalProperties()),
  );
  hasNameCollision: Signal<boolean> = computed(() =>
    DigitalPropertyCollisionUtility.hasNameCollision(this.digitalProperty, this.destinationDigitalProperties()),
  );
  isLoading: boolean = false;
  canMove: Signal<boolean> = computed(() => this.destinationWorkspaceId() && !this.hasNameCollision() && !this.hasKeyCollision());

  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    public modal: NgbActiveModal,
    private digitalPropertyService: DigitalPropertyService,
    private loadErrorHandler: LoadErrorHandler,
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onMoveConfirmed(): void {
    if (!this.canMove()) {
      return;
    }

    this.isLoading = true;
    this.subscriptions.add(
      this.digitalPropertyService
        .move(this.workspaceId, this.digitalProperty[$digitalProperty._id].toString(), this.destinationWorkspaceId())
        .subscribe({
          next: () => {
            this.isLoading = false;
            this.modal.close(this.destinationWorkspaceId());
          },
          error: (error: HttpErrorResponse) => {
            this.isLoading = false;
            this.loadErrorHandler.handleError(
              'error_updating_digital_asset_state',
              error,
              MoveDigitalPropertyModalComponent.name,
              true,
            );
          },
        }),
    );
  }

  onWorkspaceSelected(workspaceId: string): void {
    this.destinationWorkspaceId.set(workspaceId);
  }

  onCancel(): void {
    this.modal.dismiss();
  }

  protected readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  protected readonly $workspace: typeof $workspace = $workspace;
}
